// frontend/src/components/game/managers/MapManager/ObjectsLayer.jsx

import useGameStore from '@store/useGameStore';
import useSelectionStore from '@store/useSelectionStore';
import MapObjectRenderer from '@gameobjects/MapObjectRenderer';
import { EMPTY_ARRAY } from '@utils/constants';
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import classnames from 'classnames';
import './ObjectsLayer.css';

// Helper function to flatten map objects for rendering
const flattenMapObjects = (game) => {
  if (!game) return EMPTY_ARRAY;

  const objects = [];

  // 1. Static objects (background)
  if (game.static_objects) {
    Object.values(game.static_objects).forEach(category => {
      if (Array.isArray(category)) {
        objects.push(...category);
      }
    });
  }

  // 2. Interactables (middle)
  if (game.interactables) {
    Object.values(game.interactables).forEach(category => {
      if (Array.isArray(category)) {
        objects.push(...category);
      }
    });
  }

  // 3. Dynamic objects (foreground) - includes units
  if (game.dynamic_objects) {
    Object.values(game.dynamic_objects).forEach(category => {
      if (Array.isArray(category)) {
        objects.push(...category);
      }
    });
  }

  return objects;
};

const ObjectsLayer = ({ tileSize }) => {
  // Get game data and compute flattened objects locally
  const game = useGameStore.getState().game;
  const renderableObjects = flattenMapObjects(game);

  // Get handleClick from store
  const { handleClick } = useSelectionStore();

  // Get cinematic state without subscribing
  const isCinematicPhase = useGameStore.getState().isResolutionPhase;

  // Check if we're in deploy phase for undeploy button
  const isDeployPhase = game?.status === 'game_deploy';
  const currentPlayerId = useGameStore.getState().currentPlayer?.id;

  // Handle object click
  const handleObjectClick = (obj) => {
    console.log('[ObjectsLayer] Object clicked:', obj);

    // Determine clickable type based on object type
    let clickType;
    if (obj.type === 'unit') {
      clickType = ClickableType.UNIT;
    } else if (obj.type === 'structure') {
      clickType = ClickableType.STRUCTURE;
    } else {
      throw new Error(`[ObjectsLayer] Unknown object type: ${obj.type}`);
    }

    // Create ClickDTO and pass to store
    const clickDTO = new ClickDTO(clickType, obj, 'ObjectsLayer');
    handleClick(clickDTO);
  };

  const layerClasses = classnames(
    'objects-layer',
    { 'cinematic-movement': isCinematicPhase }
  );

  return (
    <div className={`map-layer ${layerClasses}`}>
      {renderableObjects.map(obj => {
        // Use centralized object data - positions already normalized based on game state
        let renderObject = obj;
        if (obj.type === 'unit' && obj.id) {
          // Use the new function that checks both deployed and available units
          const smartUnit = useGameStore.getState().getUnitOrAvailableUnitById(obj.id);
          renderObject = smartUnit || obj;
        }

        // Guard against missing coordinates
        if (renderObject.x === undefined || renderObject.y === undefined) {
          console.warn('[ObjectsLayer] Object missing coordinates:', renderObject);
          return null; // Skip rendering this object
        }

        const objectStyle = {
          left: `${renderObject.x * tileSize}px`,
          top: `${renderObject.y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        const objectClasses = classnames(
          'map-object',
          { 'cinematic-movement': isCinematicPhase && obj.type === 'unit' }
        );

        // Determine if undeploy button should show for this object
        const showUndeploy = isDeployPhase &&
          obj.type === 'unit' &&
          renderObject.player_id === currentPlayerId;

        return (
          <div
            key={`${obj.type}-${obj.id}`}
            className={objectClasses}
            style={objectStyle}
            data-object-id={obj.id}
            data-object-type={obj.type}
            onClick={() => handleObjectClick(renderObject)}
          >
            <MapObjectRenderer object={renderObject} showUndeployButton={showUndeploy} />
          </div>
        );
      })}
    </div>
  );
};

export default ObjectsLayer;